// =============================================================================
// CustomerMediaPanel — manage a customer's site-visit media (photos, videos,
// documents) stored in the private 'customer-media' bucket. Used on the
// Appointment detail (Media tab) and, in selectable mode, in the Estimate editor
// to choose photos to attach to the printed estimate.
// =============================================================================
import { useEffect, useRef, useState, useCallback } from 'react'
import { Upload, Trash2, FileText, Film, Image as ImageIcon, Loader2, Download, Check } from 'lucide-react'
import {
  listCustomerMedia, addCustomerMedia, deleteCustomerMedia, updateCustomerMedia,
} from '../services/data'
import { useAuth } from '../context/AuthContext'
import type { CustomerMedia } from '../types'
import { toast } from 'react-toastify'

const MAX_BYTES = 200 * 1024 * 1024 // 200 MB (videos)

function humanSize(n: number | null): string {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export default function CustomerMediaPanel({
  customerId,
  appointmentId = null,
  selectable = false,
  selectedIds,
  onToggleSelect,
}: {
  customerId: string
  appointmentId?: string | null
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (m: CustomerMedia) => void
}) {
  const { user } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const [media, setMedia] = useState<CustomerMedia[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    listCustomerMedia(customerId)
      .then(setMedia)
      .catch(() => toast.error('Failed to load media'))
      .finally(() => setLoading(false))
  }, [customerId])

  useEffect(load, [load])

  const onPick = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const chosen = Array.from(files)
    const tooBig = chosen.find(f => f.size > MAX_BYTES)
    if (tooBig) { toast.error(`"${tooBig.name}" is too large (max 200 MB)`); return }
    setUploading(true)
    try {
      for (const file of chosen) {
        await addCustomerMedia({ file, customerId, appointmentId, createdBy: user?.id ?? null })
      }
      toast.success(chosen.length > 1 ? `${chosen.length} files uploaded` : 'File uploaded')
      load()
    } catch (e) {
      console.error(e)
      toast.error('Upload failed — check your connection and try again')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = async (m: CustomerMedia) => {
    if (!window.confirm(`Delete "${m.title || m.file_name}"? This cannot be undone.`)) return
    try {
      await deleteCustomerMedia(m)
      setMedia(prev => prev.filter(x => x.id !== m.id))
    } catch { toast.error('Failed to delete') }
  }

  const saveTitle = async (m: CustomerMedia, title: string) => {
    if (title === (m.title ?? '')) return
    try {
      await updateCustomerMedia(m.id, { title })
      setMedia(prev => prev.map(x => x.id === m.id ? { ...x, title } : x))
    } catch { toast.error('Failed to rename') }
  }

  return (
    <div>
      <div
        className="flex items-center justify-between gap-3 mb-4 p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50"
        onDragOver={e => { e.preventDefault() }}
        onDrop={e => { e.preventDefault(); onPick(e.dataTransfer.files) }}
      >
        <div className="text-sm text-gray-500">
          Drag &amp; drop photos, videos, or documents here, or
        </div>
        <div>
          <input ref={inputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden" onChange={e => onPick(e.target.files)} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? 'Uploading…' : 'Upload Files'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading media…
        </div>
      ) : media.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">No media yet. Upload site-visit photos, videos, or documents above.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map(m => {
            const isSel = selectable && selectedIds?.has(m.id)
            const canSelect = selectable && m.kind === 'photo'
            return (
              <div key={m.id}
                className={`relative rounded-lg border overflow-hidden bg-white flex flex-col ${isSel ? 'border-green-500 ring-2 ring-green-400' : 'border-gray-200'}`}>
                {canSelect && (
                  <button type="button" onClick={() => onToggleSelect?.(m)}
                    title={isSel ? 'Remove from estimate' : 'Attach to estimate'}
                    className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full flex items-center justify-center border ${isSel ? 'bg-green-600 border-green-600 text-white' : 'bg-white/90 border-gray-300 text-transparent hover:text-gray-400'}`}>
                    <Check size={14} />
                  </button>
                )}
                <div className="h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {m.kind === 'photo' && m.file_url
                    ? <img src={m.file_url} alt={m.title ?? ''} className="object-cover w-full h-full" />
                    : m.kind === 'video' && m.file_url
                      ? <video src={m.file_url} className="object-cover w-full h-full" controls preload="metadata" />
                      : m.kind === 'video'
                        ? <Film size={36} className="text-gray-400" />
                        : <FileText size={36} className="text-gray-400" />}
                </div>
                <div className="p-2 flex flex-col gap-1 grow">
                  <input
                    defaultValue={m.title ?? ''}
                    onBlur={e => saveTitle(m, e.target.value)}
                    className="text-xs font-medium text-gray-800 w-full px-1 py-0.5 rounded border border-transparent hover:border-gray-200 focus:border-green-400 focus:outline-none"
                  />
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span className="inline-flex items-center gap-1 capitalize">
                      {m.kind === 'photo' ? <ImageIcon size={11} /> : m.kind === 'video' ? <Film size={11} /> : <FileText size={11} />}
                      {m.kind}
                    </span>
                    <span>{humanSize(m.size_bytes)}</span>
                  </div>
                  {appointmentId && m.appointment_id === appointmentId && (
                    <span className="text-[10px] text-green-700 bg-green-50 rounded px-1 py-0.5 self-start">This visit</span>
                  )}
                  <div className="flex items-center gap-2 mt-auto pt-1">
                    {m.file_url && (
                      <a href={m.file_url} target="_blank" rel="noreferrer" download={m.file_name ?? undefined}
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
                        <Download size={12} /> Open
                      </a>
                    )}
                    <button type="button" onClick={() => remove(m)}
                      className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 ml-auto">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
